import React, { useState, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist';
import MintDBLoader from './MintDBLoader.tsx';

interface GeneMutationTotal {
  gene_name: string;
  total_mutations: number;
}

interface MutationBarChartProps {
  selectedDisorders: string[];
  selectedGenes: string[];
}

const MutationBarChart: React.FC<MutationBarChartProps> = ({ selectedDisorders, selectedGenes }) => {
  const [data, setData] = useState<GeneMutationTotal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMutationData();
  }, []);

  useEffect(() => {
    if (data.length > 0 && plotRef.current && selectedGenes.length > 0) {
      renderBarChart();
    }
  }, [data, selectedGenes]);

  const fetchMutationData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/mutation-counts?type=totals');

      if (!response.ok) {
        throw new Error('Failed to fetch gene mutation data');
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching mutation data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderBarChart = () => {
    if (!plotRef.current || data.length === 0) return;
    if (selectedGenes.length === 0) return;

    // Filter data based on selected genes only (data already has totals across all disorders)
    const filteredData = data.filter(item =>
      selectedGenes.includes(item.gene_name)
    );

    if (filteredData.length === 0) {
      Plotly.purge(plotRef.current);
      return;
    }

    // Sort in descending order by total mutations
    const sortedGenes = filteredData
      .map(item => ({
        gene: item.gene_name,
        count: parseInt(item.total_mutations.toString())
      }))
      .sort((a, b) => b.count - a.count);

    const genes = sortedGenes.map(item => item.gene);
    const counts = sortedGenes.map(item => item.count);

    const barTrace = {
      x: genes,
      y: counts,
      type: 'bar',
      marker: {
        color: '#1A2940',
        opacity: 0.8,
        line: {
          color: '#0F172A',
          width: 0.5
        }
      },
      text: counts.map(c => c.toString()),
      textposition: 'outside',
      textfont: {
        size: 8,
        color: '#1A2940',
        family: 'Arial, sans-serif'
      },
      hovertemplate: '<b>Gene:</b> %{x}<br><b>Total Mutations:</b> %{y}<extra></extra>'
    };

    const layout = {
      title: {
        text: 'Unique RSID Count per Gene',
        font: {
          size: 13,
          color: '#1A2940',
          family: 'Arial, sans-serif',
          weight: 600
        },
        x: 0.5,
        xanchor: 'center'
      },
      xaxis: {
        title: {
          text: 'Gene Symbols',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        tickangle: -60,
        tickfont: {
          size: 8,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: false,
        zeroline: false,
        showline: true,
        linecolor: '#E5E7EB',
        linewidth: 1
      },
      yaxis: {
        title: {
          text: 'Total Unique RSIDS',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        tickfont: {
          size: 9,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: true,
        gridcolor: '#F3F4F6',
        gridwidth: 1,
        zeroline: false,
        showline: false
      },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      margin: {
        l: 60,
        r: 20,
        b: 100,
        t: 50,
        pad: 5
      },
      height: 400,
      autosize: true,
      hovermode: 'closest'
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      toImageButtonOptions: {
        format: 'png',
        filename: 'mutation_count_per_gene',
        height: 400,
        width: 1200,
        scale: 2
      }
    };

    Plotly.react(plotRef.current, [barTrace], layout, config);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <MintDBLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">Error Loading Mutation Data</h5>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', border: '1px solid #D1D5DB', backgroundColor: 'white', borderRadius: '4px', padding: '10px' }}>
      <div ref={plotRef} />
    </div>
  );
};

export default MutationBarChart;
